import { ModuleConfig } from '../../common/container/module'
import React, { useState, useEffect } from 'react'
import {
  Box,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import { User } from 'modules/users/models/User'
import { BoolQueryParam } from '../../common/api/Query'
import { Header } from '../../components/header/Header'
import { getUserContainer } from 'container/user-module'
import { IUserService, LOGGED_USER_SERVICE_KEY, USER_SERVICE_KEY } from 'modules/users'
import { ILoggedUserService } from 'modules/users/services/LoggedUserService'
import { getFormContainer } from '../../container/form-module'
import { Query } from '../../common/api/Query'
import { Form } from '../../modules/forms/models/Form'
import docsIcon from '../../assets/forms/docsIcon.svg'
import tableIcon from '../../assets/forms/tableIcon.svg'
import calendarIcon from '../../assets/calendar/calendar-newEvent.svg'
import close from '../../assets/table_icons/delete.svg'
import styles from './DownloadModal.module.css'

type DownloadFormat = 'csv' | 'pdf'
type DateFilter = 'all' | 'range'

type DownloadModalProps = {
  open: boolean
  form: Form | null
  onClose: () => void
}

const formContainer = getFormContainer()
const moduleUrl = (formContainer.config as ModuleConfig).moduleFullUrl
const userContainer = getUserContainer()
const userService = userContainer.get<IUserService>(USER_SERVICE_KEY)
const loggedUserService = userContainer.get<ILoggedUserService>(LOGGED_USER_SERVICE_KEY)

export const DownloadModal = ({ open, form, onClose }: DownloadModalProps): JSX.Element => {
  const { t, i18n } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()

  const [format, setFormat] = useState<DownloadFormat | null>(null)
  const [selectedPatientID, setSelectedPatientID] = useState<string>('')
  const [dateFilter, setDateFilter] = useState<DateFilter>('all')
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const [isDownloading, setIsDownloading] = useState(false)
  const [, setError] = useState<string | null>(null)
  const [patients, setPatients] = useState<User[]>([])
  const [isLoadingPatients, setIsLoadingPatients] = useState(false)

  useEffect(() => {
    if (open) {
      setFormat(null)
      setSelectedPatientID('')
      setDateFilter('all')
      setStartDate(null)
      setEndDate(null)
      setError(null)
    }
  }, [open])

  useEffect(() => {
    if (!open || !form?.id) return

    setIsLoadingPatients(true)
    const loggedUser = loggedUserService.get()
    if (!loggedUser) {
      setIsLoadingPatients(false)
      const errMsg = t('errorLoadingPatients') || 'Error al cargar los pacientes'
      setError(errMsg)
      enqueueSnackbar(errMsg, { variant: 'error' })
      return
    }

    userService
      .getFilteredList(
        new Query({
          query: [{ name: 'isPatient', value: new BoolQueryParam(true) }],
          pager: { offset: 0, limit: 1000 },
        })
      )
      .subscribe({
        next: (res) => {
          setPatients(res?.items ?? [])
          setIsLoadingPatients(false)
        },
        error: () => {
          setIsLoadingPatients(false)
          const errMsg = t('errorLoadingPatients') || 'Error al cargar los pacientes'
          setError(errMsg)
          enqueueSnackbar(errMsg, { variant: 'error' })
        },
      })
  }, [open, form?.id, t])

  const dateRangeValid = dateFilter === 'all' || (!!startDate && !!endDate)

  const canDownload =
    !!selectedPatientID && format !== null && !isDownloading && !isLoadingPatients && dateRangeValid

  const handleDownload = async () => {
    if (!canDownload || !form?.id || !format) return

    setIsDownloading(true)
    setError(null)

    const lang = i18n.language?.split('-')[0] ?? 'es'
    const params = new URLSearchParams({ lang })

    if (dateFilter === 'range' && startDate && endDate) {
      params.set('startDate', startDate.toISOString().slice(0, 10))
      params.set('endDate', endDate.toISOString().slice(0, 10))
    }

    const url = `${moduleUrl}/user-form/export/${format}/${
      form.id
    }/${selectedPatientID}?${params.toString()}`

    try {
      const response = await fetch(url, { method: 'GET', credentials: 'include' })
      if (!response.ok) throw new Error(`Error ${response.status}`)

      const blob = await response.blob()
      const objectUrl = URL.createObjectURL(blob)

      const patient = patients.find((p) => p.id === selectedPatientID)
      const patientSlug = patient
        ? `${patient.firstName}_${patient.lastName}`.replace(/\s+/g, '_')
        : selectedPatientID
      const date = new Date().toISOString().slice(0, 10)
      const filename = `formulario_${patientSlug}_${date}.${format}`

      const anchor = document.createElement('a')
      anchor.href = objectUrl
      anchor.download = filename
      document.body.appendChild(anchor)
      anchor.click()
      document.body.removeChild(anchor)
      URL.revokeObjectURL(objectUrl)

      enqueueSnackbar(t('downloadSuccess'), { variant: 'success' })
      onClose()
    } catch {
      const errMsg = t('downloadError')
      setError(errMsg)
      enqueueSnackbar(errMsg, { variant: 'error' })
    } finally {
      setIsDownloading(false)
    }
  }

  const handleClose = () => {
    if (isDownloading) return
    onClose()
  }

  if (!open) return <></>

  return (
    <div className={styles.overlay}>
      <div className={styles.containerInside}>
        <Header label={t('downloadResults')} icon={close} onClick={handleClose} />

        {isLoadingPatients ? (
          <Box className={styles.loadingContainer}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <Box className={styles.content}>
            <Box className={styles.fieldGroup}>
              <Typography className={styles.inputTitle}>{t('selectPatient')}</Typography>
              <FormControl fullWidth size="small">
                <Select
                  displayEmpty
                  value={selectedPatientID}
                  onChange={(e) => setSelectedPatientID(e.target.value)}
                  disabled={isDownloading || patients.length === 0}
                  className={styles.selectInput}
                >
                  <MenuItem disabled value="">
                    <em className={styles.placeholder}>
                      {patients.length === 0 ? t('noPatientsAvailable') : '—'}
                    </em>
                  </MenuItem>
                  {patients.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.firstName} {p.lastName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box className={styles.fieldGroup}>
              <Typography className={styles.inputTitle}>{t('selectSurvey')}</Typography>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                disabled
                className={styles.textField}
                value={form ? t(form.title) || form.title : '—'}
                InputProps={{ readOnly: true }}
              />
            </Box>

            <Box className={styles.fieldGroup}>
              <Typography className={styles.inputTitle}>{t('selectDate')}</Typography>
              <Box className={styles.radioGroup}>
                <Box
                  className={styles.radioOption}
                  onClick={() => !isDownloading && setDateFilter('all')}
                >
                  <Box
                    className={`${styles.radioCircle} ${
                      dateFilter === 'all' ? styles.radioCircleSelected : ''
                    }`}
                  />
                  <Typography className={styles.radioLabel}>{t('all')}</Typography>
                </Box>
                <Box
                  className={styles.radioOption}
                  onClick={() => !isDownloading && setDateFilter('range')}
                >
                  <Box
                    className={`${styles.radioCircle} ${
                      dateFilter === 'range' ? styles.radioCircleSelected : ''
                    }`}
                  />
                  <Typography className={styles.radioLabel}>{t('interval')}</Typography>
                </Box>
              </Box>

              {dateFilter === 'range' && (
                <Box className={styles.dateRangeRow}>
                  <Box className={styles.datePickerWrapper}>
                    <Typography className={styles.inputTitle}>{t('startDate')}</Typography>
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
                      <DatePicker
                        inputFormat="DD/MM/YYYY"
                        value={startDate}
                        disabled={isDownloading}
                        onChange={(v: any) => setStartDate(v?.toDate() ?? null)}
                        label=""
                        renderInput={(props) => (
                          <TextField
                            size="small"
                            fullWidth
                            className={styles.dateInput}
                            {...props}
                          />
                        )}
                        components={{
                          OpenPickerIcon: () => (
                            <img src={calendarIcon} alt="calendar" style={{ marginRight: 4 }} />
                          ),
                        }}
                      />
                    </LocalizationProvider>
                  </Box>
                  <Box className={styles.datePickerWrapper}>
                    <Typography className={styles.inputTitle}>{t('finishDate')}</Typography>
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
                      <DatePicker
                        inputFormat="DD/MM/YYYY"
                        value={endDate}
                        disabled={isDownloading}
                        minDate={startDate ?? undefined}
                        onChange={(v: any) => setEndDate(v?.toDate() ?? null)}
                        label=""
                        renderInput={(props) => (
                          <TextField
                            size="small"
                            fullWidth
                            className={styles.dateInput}
                            {...props}
                          />
                        )}
                        components={{
                          OpenPickerIcon: () => (
                            <img src={calendarIcon} alt="calendar" style={{ marginRight: 4 }} />
                          ),
                        }}
                      />
                    </LocalizationProvider>
                  </Box>
                </Box>
              )}
            </Box>

            <Box className={styles.fieldGroupExport}>
              <Typography className={styles.inputTitle}>{t('exportFormat')}</Typography>
              <Box className={styles.formatCardsContainer}>
                <FormatCard
                  label=".pdf"
                  icon={<img src={docsIcon} alt="PDF" />}
                  selected={format === 'pdf'}
                  disabled={isDownloading}
                  onClick={() => setFormat('pdf')}
                />
                <FormatCard
                  label=".csv"
                  icon={<img src={tableIcon} alt="CSV" />}
                  selected={format === 'csv'}
                  disabled={isDownloading}
                  onClick={() => setFormat('csv')}
                />
              </Box>
            </Box>

            <Box className={styles.buttonWrapper}>
              <button
                type="button"
                disabled={!canDownload}
                onClick={() => {
                  if (canDownload) handleDownload()
                }}
                className={`${styles.downloadButton} ${
                  canDownload ? styles.downloadButtonActive : styles.downloadButtonDisabled
                }`}
              >
                {canDownload && !isDownloading && (
                  <CheckCircleOutlineIcon className={styles.downloadButtonIcon} />
                )}
                {isDownloading && <CircularProgress size={16} className={styles.progressLoader} />}
                <span>{isDownloading ? t('downloading') : t('downloadList')}</span>
              </button>
            </Box>
          </Box>
        )}
      </div>
    </div>
  )
}

type FormatCardProps = {
  label: string
  icon: React.ReactNode
  selected: boolean
  disabled: boolean
  onClick: () => void
}

function FormatCard({ label, icon, selected, disabled, onClick }: FormatCardProps) {
  const cardClasses = [
    styles.formatCard,
    selected && styles.formatCardSelected,
    disabled && styles.formatCardDisabled,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <Box onClick={disabled ? undefined : onClick} className={cardClasses}>
      <Box className={styles.formatCardIcon}>{icon}</Box>
      <Typography className={styles.formatCardLabel}>{label}</Typography>
    </Box>
  )
}
